import React, { PropsWithChildren, useMemo, useCallback, useRef, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import DocumentMeta from 'core/components/DocumentMeta'
import generateTestId from 'utils/test-helpers'
import { trackEvent } from 'utils/tracking'

export interface ModalProps {
  open: boolean
  onClose: (e?: any) => void
  onBackdropClick?: (e?: any) => void
  onClick?: (e?: any) => void
  title?: string | React.ReactNode
  entityName?: string
  panel?: 'drawer' | 'dialog'
  info?: string | React.ReactNode
  anchorId?: string
  className?: string
  footer?: React.ReactNode
  slideFrom?: 'top' | 'right' | 'bottom' | 'left'
  maxWidth?: number
  showOverlay?: boolean
  pageClassName?: string
  footerButtonAlignment?: 'start' | 'end' | 'center'
}

export default function Modal({
  onClose,
  onClick,
  onBackdropClick,
  open = false,
  showOverlay = true,
  title = '',
  entityName = '',
  info = '',
  anchorId = 'modal-portal-root',
  footer = undefined,
  slideFrom = 'right',
  panel = 'drawer',
  maxWidth,
  children,
  className,
  pageClassName,
  footerButtonAlignment = 'end',
  ...rest
}: PropsWithChildren<ModalProps>) {
  const animationDuration = panel === 'drawer' ? 350 : 200
  const hasFooter = !!footer
  const classes = useStyles()
  const containerElem = useMemo(() => document.getElementById(anchorId), [anchorId])
  const [openState, setOpenState] = useState(false)

  useEffect(() => {
    if (open && !openState) {
      // give the first render cycle time before kicking off the animation
      setTimeout(() => {
        if (typeof title === 'string') {
          trackEvent(`${title} - Opened`)
        }
        setOpenState(true)
      }, 10)
    }
    if (!open && openState) {
      // give the last render cycle its animation duration before removal
      setTimeout(() => {
        setOpenState(false)
      }, animationDuration)
    }
  }, [open])

  const handleBackdropClick = useCallback(
    (e) => {
      onBackdropClick ? onBackdropClick(e) : onClose(e)
    },
    [onClose, onBackdropClick],
  )
  if (!open && !openState) {
    return null
  }
  const infoComponent = !info ? null : typeof info === 'string' ? (
    <Text data-testid={generateTestId('test', 'completion', 'status')} variant="body1">
      {info}
    </Text>
  ) : (
    info
  )
  const content = (
    <div
      className={clsx(classes.modalPage, pageClassName && pageClassName, {
        [classes.isNotOpen]: !open,
        [classes.isOpen]: openState && open,
        [classes.modalPageDrawer]: panel === 'drawer',
        [classes.modalPageDialog]: panel === 'dialog',
      })}
      style={{
        transitionDuration: `${animationDuration}ms`,
        transform:
          panel === 'drawer' ? getTranslateAxis(slideFrom, openState && open ? 0 : 115) : 'unset',
      }}
    >
      <DocumentMeta bodyClasses={open ? ['blur'] : undefined} />
      <div
        className={clsx({
          [classes.overlay]: showOverlay,
        })}
        onClick={handleBackdropClick}
      />
      <article
        onClick={onClick}
        className={clsx(className, classes.modalContainer, {
          open: openState && open,
          [classes.modalContainerDrawer]: panel === 'drawer',
          [classes.modalContainerDialog]: panel === 'dialog',
          [classes.modalContainerHasFooter]: hasFooter,
        })}
        style={{
          maxWidth: maxWidth ? maxWidth : panel === 'dialog' ? 500 : 800,
          transitionDuration: `${animationDuration}ms`,
        }}
        {...rest}
      >
        {(title || infoComponent) && (
          <Text
            data-testid={generateTestId(title, 'grid', 'text')}
            component="header"
            variant="h4"
            className={clsx('modal-header', classes.gridContainer, classes.modalHeader)}
          >
            <div>
              <span>{title}</span>
              {entityName && <span className={classes.entityName}>{` (${entityName})`}</span>}
            </div>
            {infoComponent}
          </Text>
        )}
        <section className={clsx('modal-body', classes.modalBody)}>{children}</section>
        {hasFooter && (
          <Text
            component="footer"
            className={clsx('modal-footer', classes.gridContainer, classes.modalFooter, {
              [classes.modalFooterStart]: footerButtonAlignment === 'start',
              [classes.modalFooterCenter]: footerButtonAlignment === 'center',
              [classes.modalFooterEnd]: footerButtonAlignment === 'end',
            })}
          >
            {footer}
          </Text>
        )}
      </article>
    </div>
  )

  return ReactDOM.createPortal(content, containerElem)
}

const getTranslateAxis = (slideFrom, value) => {
  const translateAxis = slideFrom === 'left' || slideFrom === 'right' ? 'X' : 'Y'
  const posNegAxis = slideFrom === 'left' || slideFrom === 'top' ? '-' : ''
  return `translate${translateAxis}(${posNegAxis}${value}${value === 0 ? '' : '%'})`
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  modalPage: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    display: 'grid',
    justifyItems: 'center',
    alignItems: 'center',
    alignContent: 'center',
    transitionProperty: 'transform, opacity',
    transitionTimingFunction: 'ease-out',
    opacity: 0,
  },
  modalPageDrawer: {
    justifyItems: 'end',
    alignContent: 'start',
  },
  modalPageDialog: {
    justifyItems: 'center',
    alignContent: 'center',
  },
  isNotOpen: {
    transitionTimingFunction: 'ease-in',
  },
  isOpen: {
    opacity: 1,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: hexToRgbaCss(theme.components.frame.background, 0.75),
    zIndex: 1,
    cursor: 'pointer',
  },
  modalContainer: {
    height: 'unset',
    width: '100%',
    maxHeight: 'initial',
    minWidth: 'min-content',
    backgroundColor: theme.components.card.background,
    border: `1px solid ${theme.components.card.border}`,
    boxSizing: 'border-box',
    zIndex: 100,
    transform: 'unset',
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    transitionProperty: 'transform',
    transitionTimingFunction: 'ease-out',

    '&.open': {
      transitionTimingFunction: 'ease-in',
    },

    '@media (max-width:1100px)': {
      maxWidth: 600,
    },
    '@media (max-width:800px)': {
      maxWidth: '75%',
    },
    '@media (max-width:600px)': {
      '& section.modal-body': {
        padding: '32px 16px 24px 24px',
      },
      '& header.modal-header, & footer.modal-footer': {
        padding: '0 16px 0 24px',
      },
    },
  },
  modalContainerDrawer: {
    height: '100vh',
  },
  modalContainerDialog: {
    maxHeight: '90vh',
    transform: 'scale(0)',
    '&.open': {
      transform: 'scale(1)',
    },
  },
  modalContainerHasFooter: {
    gridTemplateRows: 'max-content 1fr max-content',
  },
  gridContainer: {
    boxSizing: 'border-box',
    minHeight: 72,
    padding: '0 24px 0 32px',
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
  },
  modalHeader: {
    borderBottom: `1px solid ${theme.components.card.border}`,
    justifyContent: 'space-between',
  },
  entityName: {
    fontWeight: 'normal',
  },
  modalBody: {
    boxSizing: 'border-box',
    padding: theme.spacing(4, 4, 3, 4),
    display: 'grid',
    gap: 16,
    alignContent: 'start',
    overflow: 'auto',
    position: 'relative',
  },
  modalFooter: {
    borderTop: `1px solid ${theme.components.card.border}`,
    justifyContent: 'end',
    gap: 16,
  },
  modalFooterStart: {
    justifyContent: 'start',
  },
  modalFooterCenter: {
    justifyContent: 'center',
  },
  modalFooterEnd: {
    justifyContent: 'end',
  },
}))
